// PURPOSE: Send a personal to-do or a pointer to a visible record.
// CONTRACT: Spec 059; create then send with stable keys and visible partial failure.
// DEPENDS: Native API, catalog permissions and the shared membership picker.
// CEILING: The server authorizes the recipient and every mutation.
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './auth';
import { useCatalog } from './data';
import { newIdempotencyKey } from './api';
import { recordTitle } from './domain';
import { Dialog, ErrorNotice } from './components';
import { RelationInput } from './forms';
import type { NativeRecord } from './types';

export function useCanSendTo() {
 const catalog = useCatalog(); const { me } = useSession();
 return me.role !== 'Agent' && !!catalog.data?.models.find(m => m.name === 'todo')?.can_create;
}
export function SendToDialog({ record, onClose, onSent }: { record: NativeRecord; onClose(): void; onSent?(): void }) {
 const { api, me } = useSession(); const cache = useQueryClient();
 const [person, setPerson] = useState(''); const [note, setNote] = useState(''); const [busy, setBusy] = useState(false); const [error, setError] = useState<unknown>();
 const [partial, setPartial] = useState(false); const locked = useRef(false);
 const created = useRef<NativeRecord | undefined>(record.model === 'todo' ? record : undefined);
 const [createKey] = useState(newIdempotencyKey); const attempt = useRef<{ signature: string; key: string } | undefined>(undefined);
 async function send() {
  if (locked.current || !person) return; locked.current = true; setBusy(true); setError(undefined);
  try {
   if (!created.current) created.current = await api.create('todo', { name: recordTitle(record), link_model: record.model, link_id: record.id }, undefined, createKey);
   if (!created.current.allowed_actions.includes('send')) throw new Error('Sending is no longer available for this to-do.');
   const signature = JSON.stringify({ person, note });
   if (attempt.current?.signature !== signature) attempt.current = { signature, key: newIdempotencyKey() };
   await api.action(created.current, 'send', { to_id: person, note }, attempt.current.key);
   void cache.invalidateQueries(); onSent?.(); onClose();
  } catch (failure) { setError(failure); setPartial(record.model !== 'todo' && !!created.current); }
  finally { locked.current = false; setBusy(false); }
 }
 return <Dialog title="Send to…" description={recordTitle(record)} onClose={() => { if (!busy) onClose(); }}>
  <form onSubmit={event => { event.preventDefault(); void send(); }}>
   <fieldset disabled={busy} className="catalog-form"><div className="form-field full"><label htmlFor="field-to_id">Person</label><RelationInput field={{ name: 'to_id', label: 'Person', type: 'text', required: true, relation: 'membership', relation_roles: ['Admin', 'AE', 'Delivery', 'Worker'] }} value={person} onChange={setPerson} excludeIds={[me.id]} /></div>
    <label className="form-field full">Note (optional)<textarea value={note} onChange={event => setNote(event.target.value)} /></label>
   </fieldset>
   {!!error && <ErrorNotice error={error} />}{partial && <p role="status">The to-do is on your own list and can be sent from Home. Sending did not finish; retry here or close this dialog.</p>}
   <footer className="form-footer"><button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancel</button><button className="button primary" disabled={busy || !person}>{busy ? 'Sending…' : 'Send'}</button></footer>
  </form>
 </Dialog>;
}
